import { api, apiFetch } from './client'

export interface PricingSettings {
  brief: number
  warenpost: number
  dhl: number
  verpackung: number
  mwst: number
  fees: number
  amazon_gebuehr_pct: number
  werbung_pct: number
  puffer_pct: number
  bump_threshold: number
  /** GLS-Stufen, netto ohne Verpackung (€). */
  gls: number
  gls_uebergroesse_150: number
  gls_uebergroesse_gt150: number
  /** Shopify-Kundenversand DE (€) — wird bei Plus-Versand vom Leitpreis abgezogen. */
  shopify_versand_de: number
}

export interface CalcResult {
  shipping: number
  bumped: boolean
  /** A2: null bei ungültigem Nenner. */
  preisInkl: number | null
  /** A2: Gesamtpreis eines „Plus Versand“-Kanals (Marge ohne Versand); Ziel = landedExkl − Kundenversand. */
  landedExkl: number | null
  breakEvenOhne: number
  breakEvenInkl: number
  aktuellerGewinn: number
}

export interface PreviewRow {
  sku: string
  parent_sku: string
  name: string
  ek: number
  current_vk: number
  margin_with: number
  calc: CalcResult
}

export interface MarginConfig {
  with_shipping: number
  without_shipping: number
}

/** Settings-Schlüssel, die der Versandeinstellungen-Reiter editiert. */
export type PricingSettingKey = Exclude<keyof PricingSettings, 'fees'>

export async function getPricingSettings(): Promise<PricingSettings> {
  const res = await api.get<{ settings: PricingSettings }>('/pricing/settings/')
  return res.settings
}

export async function updatePricingSettings(
  items: { key: PricingSettingKey; value: number }[],
): Promise<PricingSettings> {
  const res = await apiFetch<{ settings: PricingSettings }>('/pricing/settings/', {
    method: 'PUT',
    body: JSON.stringify({ settings: items }),
  })
  return res.settings
}

export async function getMargin(sku: string): Promise<MarginConfig> {
  const res = await api.get<{ sku: string; margin: MarginConfig }>(
    `/pricing/margin/${encodeURIComponent(sku)}/`,
  )
  return res.margin
}

/** Erwartet UI-Brüche (0.2 = 20 %). Sendet sie als margin_pct_*-Felder. */
export async function setMargin(
  sku: string,
  margin: { with_shipping: number; without_shipping: number },
): Promise<MarginConfig> {
  const res = await apiFetch<{ sku: string; margin: MarginConfig }>(
    `/pricing/margin/${encodeURIComponent(sku)}/`,
    {
      method: 'PUT',
      body: JSON.stringify({
        margin_pct_with_shipping: margin.with_shipping,
        margin_pct_without_shipping: margin.without_shipping,
      }),
    },
  )
  return res.margin
}

export async function previewPrices(skus: string[]): Promise<{ rows: PreviewRow[]; count: number }> {
  return api.post<{ rows: PreviewRow[]; count: number }>('/pricing/preview/', { skus })
}

export async function calculateDrafts(skus: string[]): Promise<{ updated: number; rows: PreviewRow[] }> {
  return api.post<{ updated: number; rows: PreviewRow[] }>('/pricing/calculate/', { skus })
}

export async function setDraftPrice(
  sku: string,
  price_draft: number,
  target: 'base' | 'amazon' = 'base',
): Promise<{ sku: string; target: 'base' | 'amazon'; price_draft: number; price_sync_status: string }> {
  return apiFetch<{ sku: string; target: 'base' | 'amazon'; price_draft: number; price_sync_status: string }>(
    `/pricing/draft/${encodeURIComponent(sku)}/`,
    { method: 'PUT', body: JSON.stringify({ price_draft, target }) },
  )
}

// ------------------------------------------------------------
// Kanal-Preview + Preis-Strategie pro Variante (Rollout-Frontend V1)
// ------------------------------------------------------------
export type PricingStrategy = 'margin' | 'break_even'
export type StrategyTarget = 'amazon' | 'base' | 'both'
export interface ChannelStrategies { amazon: PricingStrategy; base: PricingStrategy }

export interface ChannelTarget {
  target: 'base' | 'amazon'
  policy: 'inkl' | 'exkl'
  /** A2: null = kein Ziel (Grund in `sperre`, z. B. Kundenversand unbekannt). */
  price: number | null
  /** A2: Kundenversand des Kanals (inkl 0, unbekannt null). */
  kundenversand?: number | null
  sperre?: string | null
  /** Kanal-eigener Ist-Preis (amazon ← Listing, base ← stock_items); null = unbekannt. */
  current_price: number | null
  strategy: PricingStrategy
  warning: boolean
}

export interface ChannelPreviewRow {
  sku: string
  parent_sku: string
  /** Händlerartikelnummer/"Sternnummer" (*-Präfix, familienweit identisch). Optional für Test-Fixtures. */
  merchant_sku?: string | null
  /** Eigene DreamRobot-Lagernummer DIESER Zeile (dr_stock_id, roh ohne "DR-"-Präfix). Optional für Alt-Fixtures. */
  dr_stock_id?: string | null
  /** true = die Zeile ist der Grundartikel selbst (kein Kind). Optional für Alt-Fixtures. */
  is_grundartikel?: boolean
  /** Plattform-interne stock_items.id — Join-Schlüssel für Portal-Angebote. Optional für Alt-Fixtures. */
  stock_item_id?: number
  /** Set-Menge (Ein-Komponenten-Set, z.B. 4 beim 4er-Set); null/fehlend = unbekannt/mehrteilig. */
  set_quantity?: number | null
  name: string
  ek: number
  current_vk: number
  calc: CalcResult
  targets: ChannelTarget[]
}

export interface AmazonModelAssumptions {
  vat_pct: number
  default_fee_pct: number
  advertising_pct: number
  risk_pct: number
  target_margin_pct: number
  legacy_all_in_pct: number
}

export interface AmazonFeeOverride {
  seller_sku: string
  fee_pct: number
}

export type AmazonModelReason =
  | 'unknown_ek'
  | 'invalid_cost'
  | 'invalid_assumptions'
  | 'legacy_infeasible'
  | 'detailed_infeasible'

export interface AmazonModelScenario {
  price: number | null
  residual_pct: number | null
  reason: AmazonModelReason | null
}

export interface AmazonModelPreviewRow {
  sku: string
  seller_sku: string | null
  parent_sku: string
  name: string
  ek: number
  shipping: number
  current_amazon_price: number | null
  fee_pct: number
  fee_source: 'default_assumption' | 'sku_override'
  comparison: {
    cost_base: number | null
    vat_share_pct: number | null
    current_db_before_advertising: number | null
    current_db_after_advertising_and_risk: number | null
    detailed_minus_legacy: number | null
    legacy: AmazonModelScenario
    detailed: AmazonModelScenario
  }
}

export async function amazonModelPreview(input: {
  skus: string[]
  model: AmazonModelAssumptions
  fee_overrides: AmazonFeeOverride[]
}): Promise<{ rows: AmazonModelPreviewRow[]; count: number; unused_fee_overrides: string[]; writes_performed: false }> {
  return api.post<{ rows: AmazonModelPreviewRow[]; count: number; unused_fee_overrides: string[]; writes_performed: false }>(
    '/pricing/amazon-model-preview/',
    input,
  )
}

export async function channelPreview(skus: string[]): Promise<{ rows: ChannelPreviewRow[]; count: number }> {
  return api.post<{ rows: ChannelPreviewRow[]; count: number }>('/pricing/channel-preview/', { skus })
}

export interface ChannelCalculateResult {
  updated_base: number
  updated_amazon: number
  rows: ChannelPreviewRow[]
}

/** Berechnet + speichert die Kanal-Entwürfe (base → stock_items.price_draft, amazon → Listing-Draft). */
export async function channelCalculate(skus: string[]): Promise<ChannelCalculateResult> {
  return api.post<ChannelCalculateResult>('/pricing/channel-calculate/', { skus })
}

export async function getStrategies(sku: string): Promise<ChannelStrategies> {
  const res = await api.get<{ sku: string; strategies: ChannelStrategies }>(`/pricing/strategy/${encodeURIComponent(sku)}/`)
  return res.strategies
}

/** Setzt die Strategie je Kanal; target 'both' = beide (Alt-Semantik). */
export async function setStrategy(
  sku: string,
  strategy: PricingStrategy,
  target: StrategyTarget = 'both',
): Promise<ChannelStrategies> {
  const res = await apiFetch<{ sku: string; target: StrategyTarget; strategies: ChannelStrategies }>(
    `/pricing/strategy/${encodeURIComponent(sku)}/`,
    { method: 'PUT', body: JSON.stringify({ strategy, target }) },
  )
  return res.strategies
}

export interface StrategyException {
  stock_item_id: number
  sku: string
  name: string
  amazon_break_even: boolean
  base_break_even: boolean
}

export async function getStrategyExceptions(): Promise<StrategyException[]> {
  const res = await api.get<{ exceptions: StrategyException[] }>('/pricing/strategy-exceptions/')
  return res.exceptions
}

// ------------------------------------------------------------
// Set-Preview (Verkaufspreis, Schnitt 1) — read-only. Typen 1:1 zum Backend
// (src/modules/pricing/set-preview.ts, kundenversand.ts, types.ts, service.ts).
// ------------------------------------------------------------

/** Versandstufen der Verkaufspreis-Seite; `auto` = heutige Kaskade (Brief/Warenpost/DHL). */
export const CARRIER_TIERS = ['auto', 'brief', 'warenpost', 'dhl', 'gls', 'gls_uebergroesse_150', 'gls_uebergroesse_gt150'] as const
export type CarrierTier = typeof CARRIER_TIERS[number]

/** Ein Kundenversand-Betrag, den der Kunde bei „Plus Versand" zusätzlich zahlt (Brutto, €). */
export interface KundenversandOption {
  key: string
  label: string
  betrag: number
}

/** Zerlegung des Leitpreises (Anzeige-Stapel); Summe aller Beträge = preis_inkl. */
export interface SetStack {
  ust: number
  amazon_gebuehr: number
  werbung: number
  puffer: number
  marge: number
  ek_plus_versand: number
  puffer_negativ: boolean
}

export interface SetCalcResult {
  quantity: number
  ek_total: number
  weight_g_total: number
  carrier_tier_used: Exclude<CarrierTier, 'auto'>
  carrier_tier_auto: Exclude<CarrierTier, 'auto'>
  shipping: number
  bumped: boolean
  preis_inkl: number
  break_even_inkl: number
  stack: SetStack
  /** Kundenversand-Option-Key → Leitpreis − Kundenversand (≥ 0). */
  plus_versand: Record<string, number>
  plus_versand_negativ: boolean
  /** Nenner ≤ EPS oder nicht-finite Eingabe → alle Beträge 0; UI/CSV zeigen „ungültig". */
  invalid: boolean
}

export interface SetPreviewRow {
  sku: string
  /** Sternnummer (Händlerartikelnummer, z. B. `*814`); `null` wenn nicht gepflegt. */
  merchant_sku: string | null
  name: string
  stock_item_id: number
  is_grundartikel: boolean
  margin_pct: number
  margin_source: 'config' | 'default'
  ek_unit: number
  ek_missing: boolean
  ek_zero: boolean
  weight_g: number
  weight_missing: boolean
  weight_suspect: boolean
  dims_missing: boolean
  dimensions: string
  carrier: string
  carrier_tier: CarrierTier
  sets: SetCalcResult[]
}

export interface SetPreviewResult {
  settings_used: PricingSettings
  /** Inkl. `custom`, wenn `kundenversand != null`. */
  kundenversand_optionen: KundenversandOption[]
  quantities: number[]
  rows: SetPreviewRow[]
  missing: string[]
  truncated: boolean
}

/** Request = Schema-Input (`setPreviewSchema`): genau eine Selektion (folder_ids XOR skus). */
export interface SetPreviewRequest {
  folder_ids?: number[]
  skus?: string[]
  quantities?: number[]
  kundenversand?: number | null
  /** sku → Stufe (ohne Persistenz). */
  tiers?: Record<string, CarrierTier>
}

/** Read-only Set-Preise je Ordner/SKU; die Antwort-Felder liegen top-level neben `ack`. */
export async function setPreview(input: SetPreviewRequest): Promise<SetPreviewResult> {
  return api.post<SetPreviewResult>('/pricing/set-preview/', input)
}

/** Grenzen des Backend-Vertrags; Slot-Grenze für das Kreuzprodukt A × B. */
export const KOMBI_LIMITS = { minComponents: 2, maxComponents: 10, minQty: 1, maxQty: 100, maxKombis: 100, maxSlot: 10, maxSkus: 500 } as const

export interface KombiComponent {
  sku: string
  merchant_sku: string | null
  qty: number
  ek_unit: number
  weight_g: number
  is_grundartikel: boolean
}

export interface KombiPreviewRow extends Omit<SetPreviewRow, 'sku' | 'stock_item_id' | 'is_grundartikel'> {
  key: string
  label: string
  components: KombiComponent[]
}

export interface KombiPreviewResult {
  settings_used: PricingSettings
  kundenversand_optionen: KundenversandOption[]
  quantities: number[]
  rows: KombiPreviewRow[]
  missing: { key: string; skus: string[] }[]
}

export interface KombiPreviewRequest {
  kombis: { key: string; components: { sku: string; qty: number }[] }[]
  quantities: number[]
  kundenversand: number | null
  /** Kombi-key → Stufe, ohne Persistenz. */
  tiers: Record<string, CarrierTier>
}

export async function kombiPreview(input: KombiPreviewRequest): Promise<KombiPreviewResult> {
  return api.post<KombiPreviewResult>('/pricing/kombi-preview/', input)
}

/** Read-only Artikelzahl je Ordner (`folder_id` als String → Anzahl); `api.get` entfernt nur den ACK-Umschlag. */
export async function getFolderCounts(): Promise<Record<string, number>> {
  const res = await api.get<{ counts: Record<string, number> }>('/pricing/folder-counts/')
  return res.counts
}
